import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem,
} from '@mui/material'
import {
  bulkConvertActionsRoutine,
  selectMutationLoading,
} from 'features/actions/ducks'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'
import { promisifyRoutine } from 'redux-saga-routines'
import SubmitError from './SubmitError'
import { firstErrorMessage } from 'features/actions/ducks/messages'

const bulkConvertAsync = promisifyRoutine(bulkConvertActionsRoutine)

const PRIORITIES = [
  { value: '',       label: 'Auto (from each remedial\u2019s severity)' },
  { value: 'critical', label: PRIORITY_META.critical.label },
  { value: 'high',   label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low',    label: 'Low' },
]

const dialogPaperSx = {
  borderRadius: '12px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
}
const dialogTitleSx = {
  fontSize: '18px',
  fontWeight: 600,
  color: '#0C1229',
  padding: '20px 24px',
  borderBottom: '1px solid #f2f2f2',
}
const dialogContentSx = {
  padding: '20px 24px !important',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
}
const dialogActionsSx = {
  padding: '16px 24px',
  borderTop: '1px solid #f2f2f2',
  gap: '8px',
}
const cancelButtonSx = {
  textTransform: 'none',
  fontWeight: 500,
  color: '#0C1229',
  borderColor: '#f2f2f2',
  borderRadius: '8px',
  padding: '8px 20px',
  '&:hover': {
    backgroundColor: '#f2f2f2',
    borderColor: '#0C1229',
  },
}
const submitButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  backgroundColor: '#D24724',
  borderRadius: '8px',
  padding: '8px 24px',
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#a93517' },
}

const helperTextSx = {
  fontSize: 13,
  color: '#475569',
  margin: 0,
  lineHeight: 1.5,
}

const BulkConvertDialog = ({ ids, open, onClose, onConverted, approving = false }) => {
  const dispatch = useDispatch()
  const submitting = useSelector(selectMutationLoading)
  const [submitError, setSubmitError] = useState(null)
  useEffect(() => { if (open) setSubmitError(null) }, [open])
  const [priority, setPriority] = useState('')
  const [deadline, setDeadline] = useState('')

  useEffect(() => {
    if (!open) { setPriority(''); setDeadline('') }
  }, [open])

  if (!ids?.length) return null

  const submit = async () => {
    const payload = { ids }
    if (priority) payload.priority = priority
    if (deadline) payload.deadline = deadline
    setSubmitError(null)
    try {
      await bulkConvertAsync(payload, dispatch)
      if (onConverted) onConverted()
      else onClose?.()
    } catch (e) {
      setSubmitError(firstErrorMessage(e, 'No tasks were created. Please try again.'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={dialogTitleSx}>
        {approving
          ? `Approve ${ids.length} remedial${ids.length === 1 ? '' : 's'} and create ${ids.length === 1 ? 'a task' : 'tasks'}`
          : `Create ${ids.length} task${ids.length === 1 ? '' : 's'} from remedial${ids.length === 1 ? '' : 's'}`}
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <p style={helperTextSx}>
          {ids.length === 1
            ? <>{approving ? 'This confirms it is a real problem and plans the work to fix it, in one step. ' : ''}The task gets the remedial&rsquo;s title and description, goes to the person responsible for the remedial, and is due on the remedial&rsquo;s due date unless you pick a different date below. Its priority follows the remedial&rsquo;s severity unless you choose one.</>
            : <>{approving ? 'This confirms each one is a real problem and plans the work to fix it, in one step. ' : ''}Each remedial becomes its own task on the same Building Passport, with the same title and description. Each task goes to the person responsible for that remedial, and is due on the remedial&rsquo;s due date unless you pick a different date below. Priority follows each remedial&rsquo;s severity unless you choose one.</>}
        </p>
        <TextField
          select
          label="Priority"
          fullWidth
          size="small"
          value={priority}
          onChange={e => setPriority(e.target.value)}
        >
          {PRIORITIES.map(p => (
            <MenuItem key={p.value || 'auto'} value={p.value}>{p.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Deadline (optional)"
          type="date"
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          helperText="Leaves the remedial's suggested due date when blank"
          value={deadline}
          onChange={e => setDeadline(e.target.value)}
        />
      </DialogContent>
      {submitError && <SubmitError role='alert'>{submitError}</SubmitError>}
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} variant="outlined" sx={cancelButtonSx}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={submitting}
          sx={submitButtonSx}
        >
          {approving ? 'Approve and create ' : 'Create '}{ids.length} task{ids.length === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BulkConvertDialog
